import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Trash2, Shield, Clock, CheckCircle2, Crown, CalendarPlus,
  ChevronDown, ChevronUp, UserCheck, Calendar, Settings, Gift,
} from 'lucide-react';
import { getTierBadge, getTrialInfo, formatDate } from './adminHelpers';

export const AdminUserRow = ({
  u, isSuperAdmin, canModify,
  expandedUser, setExpandedUser,
  editingReferralSettings, setEditingReferralSettings,
  referralMaxInvites, setReferralMaxInvites,
  referralRewardDays, setReferralRewardDays,
  extendingUser, setExtendingUser, extendDays, setExtendDays,
  deletingUser, setDeletingUser,
  onUpdateUser, onExtendTrial, onSaveReferralSettings, onDeleteUser,
}) => (
  <div className="p-3 sm:p-4" data-testid={`user-row-${u.id}`}>
    {/* User Header Row */}
    <div className="flex items-start sm:items-center justify-between gap-2">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-white text-sm font-medium">{u.name}</p>
          {getTierBadge(u)}
          {u.referrals?.total_accepted > 0 && (
            <Badge className="bg-cyan-500/15 text-cyan-400 border-cyan-500/30 text-[10px]">
              {u.referrals.total_accepted} referral{u.referrals.total_accepted !== 1 ? 's' : ''}
            </Badge>
          )}
        </div>
        <p className="text-slate-400 text-xs truncate mt-0.5">{u.email}</p>
        <div className="flex items-center gap-3 mt-1 text-[10px] sm:text-xs text-slate-500 flex-wrap">
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            Joined {formatDate(u.created_at)}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {getTrialInfo(u)}
          </span>
          {u.is_beta_user && (
            <span className="flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3 text-emerald-400" />
              Beta
            </span>
          )}
          {u.referred_by && (
            <span className="flex items-center gap-1 text-cyan-500">
              <UserCheck className="h-3 w-3" />
              Referred by {u.referred_by.name}
            </span>
          )}
          <span className="flex items-center gap-1 text-slate-600">
            <Gift className="h-3 w-3" />
            {u.max_referrals || 6} invites / {u.referral_reward_days || 30}d reward
          </span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {u.referrals && u.referrals.total_sent > 0 && (
          <Button variant="ghost" size="sm"
            onClick={() => setExpandedUser(expandedUser === u.id ? null : u.id)}
            className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 text-xs h-7 px-2"
            data-testid={`referral-toggle-${u.id}`}>
            {expandedUser === u.id ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            <span className="hidden sm:inline ml-1">Referrals</span>
          </Button>
        )}
        {isSuperAdmin && (
          <Button variant="ghost" size="sm"
            onClick={() => {
              if (editingReferralSettings === u.id) {
                setEditingReferralSettings(null);
              } else {
                setEditingReferralSettings(u.id);
                setReferralMaxInvites(u.max_referrals || 6);
                setReferralRewardDays(u.referral_reward_days || 30);
              }
            }}
            className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 text-xs h-7 px-2"
            title="Configure referral settings"
            data-testid={`referral-settings-btn-${u.id}`}>
            <Settings className="h-3 w-3 mr-1" />
            <span className="hidden sm:inline">Rewards</span>
          </Button>
        )}
        {canModify && (
          <>
            <Button variant="ghost" size="sm"
              onClick={() => { setExtendingUser(extendingUser === u.id ? null : u.id); setExtendDays(30); }}
              className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 text-xs h-7 px-2"
              title="Adjust trial period"
              data-testid={`extend-trial-btn-${u.id}`}>
              <CalendarPlus className="h-3 w-3 mr-1" />
              <span className="hidden sm:inline">Adjust</span>
            </Button>
            {isSuperAdmin && u.tier !== 'premium' && u.role === 'user' && (
              <Button variant="ghost" size="sm"
                onClick={() => onUpdateUser(u.id, { subscription_tier: 'premium' })}
                className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 text-xs h-7 px-2"
                title="Grant premium access">
                <Crown className="h-3 w-3 mr-1" />
                <span className="hidden sm:inline">Premium</span>
              </Button>
            )}
            {isSuperAdmin && u.tier === 'premium' && u.role === 'user' && (
              <Button variant="ghost" size="sm"
                onClick={() => onUpdateUser(u.id, { subscription_tier: 'free' })}
                className="text-slate-400 hover:text-slate-300 hover:bg-slate-500/10 text-xs h-7 px-2"
                title="Revoke premium access">
                <Shield className="h-3 w-3 mr-1" />
                <span className="hidden sm:inline">Revoke</span>
              </Button>
            )}
            {isSuperAdmin && u.role === 'user' && (
              <Button variant="ghost" size="sm"
                onClick={() => onUpdateUser(u.id, { role: 'admin' })}
                className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 text-xs h-7 px-2"
                title="Promote to Admin"
                data-testid={`promote-admin-btn-${u.id}`}>
                <Shield className="h-3 w-3 mr-1" />
                <span className="hidden sm:inline">Promote</span>
              </Button>
            )}
            {isSuperAdmin && u.role === 'admin' && (
              <Button variant="ghost" size="sm"
                onClick={() => onUpdateUser(u.id, { role: 'user' })}
                className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/10 text-xs h-7 px-2"
                title="Demote to User"
                data-testid={`demote-user-btn-${u.id}`}>
                <Shield className="h-3 w-3 mr-1" />
                <span className="hidden sm:inline">Demote</span>
              </Button>
            )}
            {isSuperAdmin && (
              <Button variant="ghost" size="sm"
                onClick={() => setDeletingUser(deletingUser === u.id ? null : u.id)}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs h-7 px-2"
                title="Remove user"
                data-testid={`delete-user-btn-${u.id}`}>
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </>
        )}
      </div>
    </div>

    {/* Referral Details (expandable) */}
    {expandedUser === u.id && u.referrals && (
      <div className="mt-3 p-3 bg-cyan-500/5 rounded-lg border border-cyan-500/20" data-testid={`referral-details-${u.id}`}>
        <p className="text-xs font-medium text-cyan-400 mb-2">
          Referral Activity ({u.referrals.total_sent} invited, {u.referrals.total_accepted} accepted)
        </p>
        {u.referrals.completed.length > 0 && (
          <div className="mb-2">
            <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Accepted</p>
            {u.referrals.completed.map((r, i) => (
              <div key={r.email || `completed-${i}`} className="flex items-center gap-2 py-1">
                <span className="text-xs text-slate-300">{r.email}</span>
                <span className="text-[10px] text-slate-600 ml-auto">{formatDate(r.completed_at)}</span>
              </div>
            ))}
          </div>
        )}
        {u.referrals.pending.length > 0 && (
          <div>
            <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Pending</p>
            {u.referrals.pending.map((r, i) => (
              <div key={r.email || `pending-${i}`} className="flex items-center gap-2 py-1">
                <span className="text-xs text-slate-400">{r.email}</span>
                <span className="text-[10px] text-slate-600 ml-auto">{formatDate(r.invited_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    )}

    {/* Referral Settings Form - Admin+ only */}
    {editingReferralSettings === u.id && isSuperAdmin && (
      <div className="mt-3 p-3 bg-amber-500/5 rounded-lg border border-amber-500/20" data-testid={`referral-settings-form-${u.id}`}>
        <p className="text-xs font-medium text-amber-400 mb-2">
          Referral Rewards for {u.name}
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-[10px] text-slate-400 uppercase tracking-wide whitespace-nowrap">Max Invites</label>
            <Input type="number" min="0" max="100"
              value={referralMaxInvites}
              onChange={(e) => setReferralMaxInvites(e.target.value)}
              className="bg-slate-800 border-slate-600 text-white w-16 h-8 text-sm"
              data-testid={`referral-max-input-${u.id}`} />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[10px] text-slate-400 uppercase tracking-wide whitespace-nowrap">Reward Days</label>
            <Input type="number" min="0" max="365"
              value={referralRewardDays}
              onChange={(e) => setReferralRewardDays(e.target.value)}
              className="bg-slate-800 border-slate-600 text-white w-16 h-8 text-sm"
              data-testid={`referral-days-input-${u.id}`} />
          </div>
          <Button size="sm" onClick={() => onSaveReferralSettings(u.id)}
            className="bg-amber-600 hover:bg-amber-700 h-8 text-xs"
            data-testid={`referral-save-btn-${u.id}`}>
            Save
          </Button>
          <Button variant="ghost" size="sm"
            onClick={() => setEditingReferralSettings(null)}
            className="text-slate-400 hover:text-white h-8 text-xs">
            Cancel
          </Button>
        </div>
        <p className="text-[10px] text-slate-500 mt-1.5">
          Per year: max {referralMaxInvites} invites, each worth {referralRewardDays} bonus days for both parties
        </p>
      </div>
    )}

    {/* Adjust Trial Inline Form */}
    {extendingUser === u.id && (
      <div className="mt-3 p-3 bg-slate-700/50 rounded-lg border border-slate-600" data-testid={`extend-form-${u.id}`}>
        <p className="text-xs text-slate-400 mb-2">Adjust trial for <span className="text-white">{u.name}</span></p>
        <div className="flex items-center gap-2">
          <Input type="number" min="1" max="365" value={extendDays}
            onChange={(e) => setExtendDays(e.target.value)}
            className="bg-slate-800 border-slate-600 text-white w-20 h-8 text-sm"
            data-testid={`extend-days-input-${u.id}`} />
          <span className="text-xs text-slate-400">days</span>
          <Button size="sm" onClick={() => onExtendTrial(u.id, false)}
            className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs"
            data-testid={`extend-confirm-btn-${u.id}`}>+ Add</Button>
          <Button size="sm" onClick={() => onExtendTrial(u.id, true)}
            className="bg-red-600 hover:bg-red-700 h-8 text-xs"
            data-testid={`reduce-confirm-btn-${u.id}`}>- Reduce</Button>
          <Button variant="ghost" size="sm" onClick={() => setExtendingUser(null)}
            className="text-slate-400 hover:text-white h-8 text-xs">Cancel</Button>
        </div>
        <p className="text-[10px] text-slate-500 mt-1.5">
          {u.trial_active ? `Currently ${u.trial_days_remaining} days remaining` : 'Trial expired — adding days starts from today'}
        </p>
      </div>
    )}

    {/* Delete Confirmation */}
    {deletingUser === u.id && (
      <div className="mt-3 p-3 bg-red-500/10 rounded-lg border border-red-500/30" data-testid={`delete-confirm-${u.id}`}>
        <p className="text-xs text-red-400 font-medium mb-2">
          Permanently delete {u.name}'s account?
        </p>
        <p className="text-[10px] text-slate-500 mb-3">
          This will remove their account, all trips, documents, expenses, and referral data. This action cannot be undone.
        </p>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => onDeleteUser(u.id)}
            className="bg-red-600 hover:bg-red-700 h-8 text-xs"
            data-testid={`delete-confirm-btn-${u.id}`}>
            Yes, Delete Permanently
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setDeletingUser(null)}
            className="text-slate-400 hover:text-white h-8 text-xs">Cancel</Button>
        </div>
      </div>
    )}
  </div>
);
